import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { CalendarClock, Plug, SquarePen, type LucideIcon } from "lucide-react";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { cn } from "@/lib/utils";
import { useIsHydrated } from "./skeletonLayout";
import { readCachedAutomationsEnabled } from "./useCachedAutomationsEnabled";

/** Primary navigation pinned above the session lists. */

const NAV_ROW = "group flex h-8 w-full items-center gap-2 rounded-md border border-transparent p-1.5 type-body-s-regular cursor-pointer transition-none text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950 focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white";
const NAV_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";

// The shortcut keycaps stay hidden until the row is hovered or
// keyboard-focused, keeping the resting sidebar clean. Opacity (not display)
// preserves the row's layout so the label doesn't shift when they appear.
function ShortcutChip(t0) {
  const $ = _c(2);
  const {
    shortcut
  } = t0;
  let t1;
  if ($[0] !== shortcut) {
    t1 = <ShortcutKeys keys={Array.from(shortcut)} className="shrink-0 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />;
    $[0] = shortcut;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function RowContents(t0) {
  const $ = _c(10);
  const {
    icon: Icon,
    label,
    shortcut
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="size-[18px] shrink-0" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="flex-1 truncate text-left">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== shortcut) {
    t3 = shortcut ? <ShortcutChip shortcut={shortcut} /> : null;
    $[4] = shortcut;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export interface SidebarPrimaryNavProps {
  /** Starts a new Codesmith session (same action as the old toolbar new-chat). */
  onNewSession: () => void;
  /** Resolved automations route, or null/undefined to hide the row (flag off). */
  automationsHref?: string | null;
  onPrefetchAutomations?: () => void;
  /** Resolved MCP Connections route. */
  mcpConnectionsHref: string;
  /**
   * Called after a subpage row navigates (e.g. to collapse the sidebar on
   * mobile). The New session row deliberately does NOT call this: its mobile
   * collapse and write-blocked gating are owned by `onNewSession`, so invoking
   * `onNavigate` there too would double-fire the collapse (and on mobile toggle
   * the sidebar back open).
   */
  onNavigate?: () => void;
  newSessionShortcut?: string;
  automationsShortcut?: string;
  /** Highlights the row matching the current Codesmith subpage. */
  activeRow?: "new-session" | "automations" | "mcp-connections" | null;
  className?: string;
}
export function SidebarPrimaryNav(t0) {
  const $ = _c(33);
  const {
    onNewSession,
    automationsHref,
    onPrefetchAutomations,
    mcpConnectionsHref,
    onNavigate,
    newSessionShortcut: t1,
    automationsShortcut: t2,
    activeRow: t3,
    className
  } = t0;
  const newSessionShortcut = t1 === undefined ? "\u2325\u21E7N" : t1;
  const automationsShortcut = t2 === undefined ? "\u2325\u21E7A" : t2;
  const activeRow = t3 === undefined ? null : t3;
  const router = useRouter();
  const org = router.query.org;
  const newSessionHref = typeof org === "string" ? `/${org}/sessions/new` : "/sessions/new";
  let t4;
  if ($[0] !== className) {
    t4 = cn("flex flex-col gap-1 px-2 pt-app-header-gap pb-2", className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== onNewSession) {
    t5 = e => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      onNewSession();
    };
    $[2] = onNewSession;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = activeRow === "new-session" ? "page" : undefined;
  const t7 = activeRow === "new-session" && NAV_ROW_ACTIVE;
  let t8;
  if ($[4] !== t7) {
    t8 = cn(NAV_ROW, t7);
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== newSessionShortcut) {
    t9 = <RowContents icon={SquarePen} label="New session" shortcut={newSessionShortcut} />;
    $[6] = newSessionShortcut;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== newSessionHref || $[9] !== t5 || $[10] !== t6 || $[11] !== t8 || $[12] !== t9) {
    t10 = <Link href={newSessionHref} onClick={t5} aria-keyshortcuts="Alt+Shift+N" aria-current={t6} className={t8}>{t9}</Link>;
    $[8] = newSessionHref;
    $[9] = t5;
    $[10] = t6;
    $[11] = t8;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== activeRow || $[15] !== automationsHref || $[16] !== automationsShortcut || $[17] !== onNavigate || $[18] !== onPrefetchAutomations) {
    t11 = automationsHref ? <Link href={automationsHref} onMouseEnter={onPrefetchAutomations} onFocus={onPrefetchAutomations} onClick={onNavigate} aria-keyshortcuts="Alt+Shift+A" aria-current={activeRow === "automations" ? "page" : undefined} className={cn(NAV_ROW, activeRow === "automations" && NAV_ROW_ACTIVE)}><RowContents icon={CalendarClock} label="Automations" shortcut={automationsShortcut} /></Link> : null;
    $[14] = activeRow;
    $[15] = automationsHref;
    $[16] = automationsShortcut;
    $[17] = onNavigate;
    $[18] = onPrefetchAutomations;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = activeRow === "mcp-connections" ? "page" : undefined;
  const t13 = activeRow === "mcp-connections" && NAV_ROW_ACTIVE;
  let t14;
  if ($[20] !== t13) {
    t14 = cn(NAV_ROW, t13);
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <RowContents icon={Plug} label="MCP Connections" />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== mcpConnectionsHref || $[24] !== onNavigate || $[25] !== t12 || $[26] !== t14) {
    t16 = <Link href={mcpConnectionsHref} onClick={onNavigate} aria-current={t12} className={t14}>{t15}</Link>;
    $[23] = mcpConnectionsHref;
    $[24] = onNavigate;
    $[25] = t12;
    $[26] = t14;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t16 || $[31] !== t4) {
    t17 = <nav className={t4}>{t10}{t11}{t16}</nav>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t16;
    $[31] = t4;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
}

/**
 * Non-interactive twin of SidebarPrimaryNav for the sidebar skeleton: these
 * rows are static chrome, so the skeleton renders them for real instead of
 * shimmer blocks and the top of the sidebar doesn't shift or pop when the
 * live sidebar mounts. The Automations row mirrors the live sidebar's
 * pre-flag fallback by reading the same per-org cache.
 */
export function SidebarPrimaryNavStatic(t0) {
  const $ = _c(12);
  const {
    className
  } = t0;
  const router = useRouter();
  const org = router.query.org;
  const isHydrated = useIsHydrated();
  let t1;
  if ($[0] !== isHydrated || $[1] !== org) {
    t1 = isHydrated && readCachedAutomationsEnabled(typeof org === "string" ? org : null);
    $[0] = isHydrated;
    $[1] = org;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const showAutomations = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("flex flex-col gap-1 px-2 pt-app-header-gap pb-2", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={NAV_ROW}><RowContents icon={SquarePen} label="New session" /></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== showAutomations) {
    t4 = showAutomations && <div className={NAV_ROW}><RowContents icon={CalendarClock} label="Automations" /></div>;
    $[6] = showAutomations;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className={NAV_ROW}><RowContents icon={Plug} label="MCP Connections" /></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t4) {
    t6 = <nav className={t2}>{t3}{t4}{t5}</nav>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}